<template>
  <div class="box collapsible-card" v-if="filteredInvites.length">
    <div class="card-header-wrapper" @click="toggleCard">
      <h1 class="title is-3 card-title">Pending Invites</h1>
      <b-icon :icon="isExpanded ? 'chevron-up' : 'chevron-down'" class="toggle-icon"></b-icon>
    </div>
    <div class="card-content-wrapper" :class="{ 'is-collapsed': !isExpanded }">
      <hr />
      <b-table
        :data="filteredInvites"
        sort-icon="chevron-up"
        sort-icon-size="is-small"
        :default-sort="['name', 'asc']"
        striped
        :paginated="filteredInvites.length > 10"
        per-page="10"
        hoverable
        mobile-cards
        mobile-breakpoint="1024px"
      >
        <b-table-column label="Invitee Email" v-slot="props">
          {{ props.row.email }}
        </b-table-column>

        <b-table-column label="Invited By" v-slot="props">
          {{ props.row.user.first_name }} {{ props.row.user.last_name }}
        </b-table-column>

        <b-table-column field="created_at" label="Invited Date" v-slot="props">
          {{ new Date(props.row.created_at).toLocaleDateString() }}
        </b-table-column>
        <b-table-column
          label="Actions"
          header-class="has-text-centered"
          cell-class="has-text-centered"
          v-slot="props"
        >
          <div class="action-buttons">
            <b-button
              type="is-danger is-light"
              size="is-small"
              @click="onRevoke(props.row)"
              class="action-btn"
            >
              Revoke
            </b-button>
            <b-button
              type="is-info is-light"
              size="is-small"
              @click="onResend(props.row)"
              class="action-btn"
            >
              Resend
            </b-button>
          </div>
        </b-table-column>
      </b-table>
    </div>
  </div>
</template>

<script>
import request from '../request';

export default {
  name: 'InviteList',
  data() {
    return {
      loading: true,
      invites: [],
      isExpanded: window.innerWidth > 1024,
      isMobile: window.innerWidth <= 1024,
    };
  },
  props: {
    isForAdmin: {
      type: Boolean,
      default: false,
    },
    organization: {
      type: Object,
      default: () => ({}),
    },
  },
  mounted() {
    this.getInvites();
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  computed: {
    filteredInvites() {
      return this.invites.filter((o) => o.revoked_at === null && o.accepted_at === null);
    },
  },
  methods: {
    toggleCard() {
      if (this.isMobile) {
        this.isExpanded = !this.isExpanded;
      }
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 1024;
      if (!this.isMobile) {
        this.isExpanded = true;
      }
    },
    getInvites() {
      this.loading = true;
      const url = this.isForAdmin
        ? `/admin/invites?organization_id=${this.organization.id}`
        : '/invites';

      request
        .get(url)
        .then((resp) => (this.invites = resp.data.invites))
        .finally(() => (this.loading = false));
    },
    onRevoke(invite) {
      const url = this.isForAdmin ? `/admin/invites/${invite.id}` : `/invites/${invite.id}`;

      this.$buefy.dialog.confirm({
        message: `Are you sure you want to revoke invitation for "${invite.email}"?`,
        onConfirm: () => {
          request
            .put(url, { revoke: true })
            .then(() => this.getInvites())
            .finally(() => {
              let msg = 'Invitation revoked!';
              this.$buefy.toast.open({ message: msg, type: 'is-success' });
            });
        },
      });
    },
    onResend(invite) {
      const url = this.isForAdmin ? `/admin/invites/${invite.id}` : `/invites/${invite.id}`;

      this.$buefy.dialog.confirm({
        message: `Are you sure you want to resend the invitation for "${invite.email}"?`,
        onConfirm: () => {
          request.put(url, { resend: true }).finally(() => {
            let msg = 'Invitation sent!';
            this.$buefy.toast.open({ message: msg, type: 'is-success' });
          });
        },
      });
    },
  },
};
</script>

<style scoped>
.card-header-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.card-title {
  margin-bottom: 0 !important;
  flex-grow: 1;
}

@media (max-width: 1024px) {
  .collapsible-card {
    border-left: 4px solid #7957d5;
  }

  .card-header-wrapper {
    min-height: 1.5rem;
    padding: 0.1rem 0;
  }

  .card-title {
    font-weight: 600 !important;
    color: #363636;
  }
}

.toggle-icon {
  display: none;
  color: #6c757d;
  flex-shrink: 0;
}

.card-content-wrapper {
  overflow: hidden;
  transition:
    max-height 0.3s ease,
    opacity 0.3s ease;
  max-height: 2000px;
  opacity: 1;
}

:deep(.action-buttons) {
  display: flex;
  gap: 0.5rem;
}

@media (max-width: 1024px) {
  .card-header-wrapper {
    cursor: pointer;
  }

  .card-title {
    font-size: 1.25rem !important;
  }

  .toggle-icon {
    display: block;
  }

  .card-content-wrapper.is-collapsed {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
  }

  :deep(.action-buttons) {
    flex-direction: row;
    justify-content: center;
  }

  :deep(.action-buttons .action-btn) {
    flex: 1;
  }

  /* Force mobile card view */
  :deep(.b-table .table-wrapper) {
    overflow-x: visible;
  }

  :deep(.b-table .table) {
    display: block;
  }

  :deep(.b-table .table thead) {
    display: none;
  }

  :deep(.b-table .table tbody) {
    display: block;
  }

  :deep(.b-table .table tr) {
    display: block;
    margin-bottom: 1rem;
    border: 1px solid #dbdbdb;
    border-radius: 4px;
    padding: 0.75rem;
    background: #fff;
    box-shadow: 0 2px 3px rgba(10, 10, 10, 0.1);
  }

  :deep(.b-table .table td) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0;
    border: none;
    border-bottom: 1px solid #f0f0f0;
  }

  :deep(.b-table .table td:last-child) {
    border-bottom: none;
  }

  :deep(.b-table .table td::before) {
    content: attr(data-label);
    font-weight: 600;
    margin-right: 1rem;
    color: #363636;
  }
}
</style>
